import { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Col, Row, Table, Typography } from 'antd'
import Chart from 'react-apexcharts'
import ChartTableSwitch from './ChartTableSwitch'
import dayjs from '../../lib/datetime'

const { Title } = Typography

export default function ReportsStats({ reports }) {
  const [viewMode, setViewMode] = useState('chart')

  const chartOptions = useMemo(() => ({
    chart: {
      type: 'area',
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'inherit',
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: 'straight',
      width: 2.5,
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.25,
        opacityTo: 0.02,
        stops: [0, 95, 100],
      },
    },
    grid: {
      borderColor: '#f1f5f9',
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
    },
    markers: {
      size: 3,
      strokeWidth: 2,
      strokeColors: '#fff',
      hover: { size: 5 },
    },
    xaxis: {
      categories: reports.map(report =>
        dayjs(`${report.year}-${String(report.month).padStart(2, '0')}-01`).format('MMM YY'),
      ),
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { colors: '#94a3b8', fontSize: '11px' } },
    },
    yaxis: {
      labels: {
        formatter: value => Math.round(value).toLocaleString(),
        style: { colors: '#94a3b8', fontSize: '11px' },
      },
      min: 0,
    },
    legend: {
      position: 'bottom',
      fontSize: '12px',
      fontWeight: 500,
      labels: { colors: '#64748b' },
      markers: { radius: 3 },
    },
    colors: ['#3b82f6', '#f59e0b', '#22c55e'],
    tooltip: {
      shared: true,
      intersect: false,
    },
  }), [reports])

  const chartSeries = [
    {
      name: 'Remote Checks',
      data: reports.map(report => report.remoteChecksCount || 0),
    },
    {
      name: 'Alert Checks',
      data: reports.map(report => report.alertsCount || 0),
    },
    {
      name: 'Office Checks',
      data: reports.map(report => report.officeChecksCount || 0),
    },
  ]

  const tableColumns = [
    {
      key: 'month',
      title: 'Month',
      render: (_, record) =>
        dayjs(`${record.year}-${String(record.month).padStart(2, '0')}-01`).format('MMM YYYY'),
    },
    {
      key: 'remoteChecksCount',
      title: 'Remote Checks',
      dataIndex: 'remoteChecksCount',
      align: 'center',
      render: value => (value ?? 0).toLocaleString(),
    },
    {
      key: 'alertsCount',
      title: 'Alert Checks',
      dataIndex: 'alertsCount',
      align: 'center',
      render: value => (value ?? 0).toLocaleString(),
    },
    {
      key: 'checksCount',
      title: 'Office Checks',
      dataIndex: 'officeChecksCount',
      align: 'center',
      render: value => (value ?? 0).toLocaleString(),
    },
  ]

  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          {/* level 2 for heading order; the inline style pins the visual size */}
          <Title
            level={2}
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 600,
              color: '#0f172a',
              letterSpacing: '-0.005em',
            }}
          >
            Device checks
          </Title>
        </Col>
        <Col>
          <ChartTableSwitch onChange={setViewMode} viewMode={viewMode} />
        </Col>
      </Row>

      {viewMode === 'chart' && (
        <Chart
          options={chartOptions}
          series={chartSeries}
          type="area"
          height={300}
        />
      )}

      {viewMode === 'table' && (
        <Table
          columns={tableColumns}
          dataSource={reports.map((report, idx) => ({ ...report, key: idx }))}
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
        />
      )}
    </>
  )
}

ReportsStats.propTypes = {
  reports: PropTypes.arrayOf(
    PropTypes.shape({
      cycle: PropTypes.shape({
        id: PropTypes.number.isRequired,
        startsAt: PropTypes.string.isRequired,
        endsAt: PropTypes.string.isRequired,
      }).isRequired,
      reportsCount: PropTypes.number,
      checksCount: PropTypes.number,
      totalCount: PropTypes.number,
    }),
  ).isRequired,
}
